<template>
    <div class="input-group">
        <span v-if="currencySymbol" class="input-group-text">{{ currencySymbol }}</span>
        <input ref="input" type="text" class="form-control" 
            :value="displayValue" 
            :disabled="isDisabled" 
            :class="classMap"
            @keydown="onKeydown" 
            @input="onInput"
            @focus="onFocusInput"
            @blur="formatOnBlur"
            @paste="onPaste"
            :placeholder="placeholder"/>
    </div>
    
</template>

<script>
const NOOP = function () { };

export default {
    name: "CommonMoneyInput",

    props: {
        modelValue: {
            type: [Number, String],
            default: ""
        },

        decimal: {
            type: String,
            default: "."
        },

        thousands: {
            type: String,
            default: ","
        },

        scale: {
            type: Number,
            default: 2
        },

        fixed: {
            type: Boolean,
            default: true
        },

        positiveOnly: {
            type: Boolean,
            default: false
        },

        onInvalidKey: {
            type: Function,
            default: NOOP
        },

        onFocus: {
            type: Function,
            default: NOOP
        },

        currencySymbol:{
            type: String,
            default: ""
        },

        placeholder:{
            type: String,
            default: ""
        },

        isDisabled:{
            default: false
        },

        classMap: {
            default: ''
        }
    },

    data() {
        return {
            displayValue: "",
            isFocused: false,
            shortcuts:{
                k: 1e3,
                m: 1e6,
                b: 1e9
            }
        };
    },

    mounted() {
        this.displayValue = this.format(this.modelValue);
    },

    watch: {
        modelValue(val) {
            if (this.isFocused) return;

            const formatted = this.format(val);
            if (formatted !== this.displayValue) {
                this.displayValue = formatted;
            }
        }
    },

    methods: {
        /* -----------------------------
         * Internal normalization
         * ----------------------------- */

        stripFormatting(value) {
            if (value === null || value === undefined) return "";

            return String(value)
                .split(this.thousands).join("")
                .replace(this.decimal, ".");
        },

        toNumber(value) {
            const n = Number(this.stripFormatting(value));
            return isNaN(n) ? null : n;
        },

        addThousands(value) {
            return value.replace(/\B(?=(\d{3})+(?!\d))/g, this.thousands);
        },

        format(value) {
            if (value === "" || value === null || value === undefined) {
                return "";
            }

            const n = typeof value === "number" ? value : this.toNumber(value);
            if (n === null) return "";

            const negative = n < 0;
            const abs = Math.abs(n);

            let parts = this.fixed
                ? abs.toFixed(this.scale).split(".")
                : String(abs).split(".");

            let intPart = this.addThousands(parts[0]);
            let decPart = parts[1] || "";

            let result = this.scale > 0
                ? intPart + this.decimal + decPart
                : intPart;

            return negative && !this.positiveOnly ? "-" + result : result;
        },

        /* -----------------------------
         * Caret helpers
         * ----------------------------- */

        getCaret() {
            return this.$refs.input.selectionStart;
        },

        setCaret(pos) {
            this.$refs.input.setSelectionRange(pos, pos);
        },

        /* -----------------------------
         * Keyboard handling (mirrored)
         * ----------------------------- */

        onKeydown(e) {
            const key = e.key;

            // allow ctrl/cmd shortcuts
            if (e.ctrlKey || e.metaKey) {
                return;
            }

            // navigation keys
            if (
                key === "Backspace" ||
                key === "Delete" ||
                key === "ArrowLeft" ||
                key === "ArrowRight" ||
                key === "Tab" ||
                key === "Home" ||
                key === "End"
            ) {
                return;
            }

            // shortcuts: k / m / b
            const lower = key.toLowerCase();
            if (this.shortcuts[lower]) {
                e.preventDefault();
                this.applyShortcut(this.shortcuts[lower]);
                return;
            }

            // minus sign
            if (key === "-" && !this.positiveOnly) {
                if (this.displayValue.indexOf("-") !== -1) {
                    e.preventDefault();
                    this.onInvalidKey(key);
                }
                return;
            }

            // decimal character
            if (key === this.decimal) {
                if (this.scale === 0 || this.displayValue.indexOf(this.decimal) !== -1) {
                    e.preventDefault();
                    this.onInvalidKey(key);
                }
                return;
            }

            // digits only
            if (!/^\d$/.test(key)) {
                e.preventDefault();
                this.onInvalidKey(key);
            }
        },

        onPaste(e) {
            e.preventDefault();

            const text = (e.clipboardData || window.clipboardData).getData("text");
            const number = this.toNumber(text);

            if (number === null) return;

            this.displayValue = this.format(text);
            this.$emit("update:modelValue", number);
        },

        onInput(e) {
            const caret = this.getCaret();
            const raw = e.target.value;
            const number = this.toNumber(raw);

            if (number === null && raw !== "" && raw !== "-") {
                return;
            }

            this.displayValue = raw;
            this.$emit("update:modelValue", number);

            this.$nextTick(function () {
                this.setCaret(caret);
            });
        },

        onFocusInput() {
            this.isFocused = true;
            this.displayValue = this.stripFormatting(this.displayValue);
        },

        formatOnBlur() {
            this.isFocused = false;
            this.displayValue = this.format(this.displayValue);
        },

        applyShortcut(multiplier) {
            const base = this.toNumber(this.displayValue) || 0;
            const value = base * multiplier;

            this.$emit("update:modelValue", value);
            this.displayValue = this.format(value);
        }
    }
};
</script>
